import React, { useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { CARD_SX, HAIRLINE_HOVER_COLOR, INNER_RADIUS, PILL_RADIUS } from '../../agent'
import Tree from '../tree/Tree'
import AlertSchedulePicker from './AlertSchedulePicker'
import AlertRecipientsField from './AlertRecipientsField'
import { getInitialExpandedIds } from '../../utils/tree'
import { getNodeTitle } from '../../utils/nodeTypes'
import type { GenNode } from '../../types/carbon/models/GenNode'
import type { AlertSchedule } from '../../types/gotypes'
import type { AlertRecipient, AlertTableInput } from '../../hooks/useAlerts'
import type { AlertDraftProblems, AlertSetupDraft, AlertSetupKind } from '../../utils/alertSetupDraft'

export interface AlertSetupCardJob {
  name: string
  displayName?: string
}

export interface AlertSetupCardProps {
  draft: AlertSetupDraft
  /** True for a new alert; false when the card changes a saved one (its kind is then fixed). */
  isNew: boolean
  /** Why each field cannot be saved yet; shown once Send has been pressed. */
  problems: AlertDraftProblems
  jobs: AlertSetupCardJob[]
  toc: GenNode[]
  tocLoading: boolean
  recipientOptions: AlertRecipient[]
  recipientsLoading: boolean
  recipientsError: string | null
  recipientQuery: string
  /** The saved alert is still loading (`?alert=<id>`). */
  loading: boolean
  loadError: string | null
  /** Why this card cannot be used (a colleague's alert, an event alert). */
  lockedReason?: string
  /** Send is under way: every field holds still. */
  disabled: boolean
  onChange: (patch: Partial<Pick<AlertSetupDraft, 'name' | 'question' | 'condition' | 'context' | 'schedule' | 'recipients'>>) => void
  onKindChange: (kind: AlertSetupKind) => void
  onJobChange: (job: string) => void
  onAddTable: (table: AlertTableInput) => void
  onRemoveTable: (table: AlertTableInput) => void
  onRecipientQueryChange: (q: string) => void
}

export const ALERT_CARD_INTRO =
  'Nothing is saved until you press Send. Then the alert is saved paused, and the agent writes and tests the check with you and asks before turning it on.'

const KINDS: { value: AlertSetupKind; label: string }[] = [
  { value: 'table', label: 'Saved tables' },
  { value: 'query', label: "A question about the job's data" },
]

const tablePath = (t: AlertTableInput) => (t.folder_path ? `${t.folder_path}/${t.filename}` : t.filename)

const SMALL_FIELD_SX = { '& .MuiInputBase-input': { fontSize: 13 } }

/**
 * The New alert form, as a card above the start screen's composer (alerts
 * round, 12 Oct). It carries everything the rule needs; the composer under
 * it (with its microphone) adds anything else the user wants to say. The
 * card is a form only: its contents live in useAlertSetupDraft on the page,
 * so a failed Send, or leaving and coming back, loses nothing.
 */
const AlertSetupCard: React.FC<AlertSetupCardProps> = ({
  draft,
  isNew,
  problems,
  jobs,
  toc,
  tocLoading,
  recipientOptions,
  recipientsLoading,
  recipientsError,
  recipientQuery,
  loading,
  loadError,
  lockedReason,
  disabled,
  onChange,
  onKindChange,
  onJobChange,
  onAddTable,
  onRemoveTable,
  onRecipientQueryChange,
}) => {
  // Which folders are open and which node is highlighted: the tree's own
  // view state, not the alert's.
  const [expanded, setExpanded] = useState<string[]>([])
  const [selected, setSelected] = useState('')
  const expandedInitRef = useRef<GenNode[] | null>(null)
  if (toc.length > 0 && expandedInitRef.current !== toc) {
    expandedInitRef.current = toc
    setExpanded(getInitialExpandedIds(toc))
  }

  const off = disabled || loading || Boolean(lockedReason) || Boolean(loadError)

  const handleNodeSelect = (itemId: string, node: GenNode, isSelected: boolean) => {
    if (!isSelected) return
    setSelected(itemId)
    if (node.type !== 'Table') return
    onAddTable({ job: draft.job, filename: node.value1 || '', folder_path: node.value2 || '', display_name: getNodeTitle(node) })
  }

  const handleNodeExpand = (itemId: string, _node: GenNode, isExpanded: boolean) => {
    setExpanded(isExpanded ? [...expanded, itemId] : expanded.filter((id) => id !== itemId))
  }

  return (
    <Box
      data-testid="alert-setup-card"
      role="group"
      aria-label={isNew ? 'New alert' : 'Change alert'}
      sx={{ ...CARD_SX, p: 2, maxHeight: '100%', minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Box>
        <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem' }}>
          {isNew ? 'New alert' : `Change alert: ${draft.name || '…'}`}
        </Typography>
        <Typography sx={{ color: '#4b5563', fontSize: 13 }}>{ALERT_CARD_INTRO}</Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}><CircularProgress size={20} /></Box>
      )}
      {loadError && <Alert severity="error" sx={{ fontSize: 13 }}>{loadError}</Alert>}
      {lockedReason && <Alert severity="info" sx={{ fontSize: 13 }}>{lockedReason}</Alert>}

      <Box role="group" aria-label="What to watch" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {KINDS.map((k) => {
          const chosen = draft.kind === k.value
          return (
            <Button
              key={k.value}
              size="small"
              variant={chosen ? 'contained' : 'text'}
              color={chosen ? 'info' : 'inherit'}
              aria-pressed={chosen}
              disabled={off || (!isNew && !chosen)}
              onClick={() => onKindChange(k.value)}
              startIcon={chosen ? <CheckIcon sx={{ fontSize: 14 }} /> : undefined}
              disableElevation
              sx={{
                textTransform: 'none',
                borderRadius: PILL_RADIUS,
                px: 2,
                fontSize: '0.75rem',
                border: '1px solid',
                borderColor: chosen ? 'info.main' : 'divider',
                color: chosen ? 'white' : 'text.secondary',
                '&:hover': chosen ? {} : { backgroundColor: 'action.hover', borderColor: HAIRLINE_HOVER_COLOR, color: 'text.primary' },
              }}
            >
              {k.label}
            </Button>
          )
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          label="Name"
          size="small"
          value={draft.name}
          onChange={(e) => onChange({ name: e.target.value })}
          disabled={off}
          required
          error={Boolean(problems.name)}
          helperText={problems.name}
          sx={{ flex: '2 1 220px', ...SMALL_FIELD_SX }}
        />
        <FormControl size="small" required disabled={off} error={Boolean(problems.job)} sx={{ flex: '1 1 160px' }}>
          <InputLabel id="alert-card-job-label">Job</InputLabel>
          <Select
            labelId="alert-card-job-label"
            label="Job"
            value={jobs.some((j) => j.name === draft.job) ? draft.job : ''}
            onChange={(e) => onJobChange(String(e.target.value))}
            sx={{ fontSize: 13 }}
          >
            {jobs.map((j) => (
              <MenuItem key={j.name} value={j.name}>{j.displayName || j.name}</MenuItem>
            ))}
          </Select>
          {problems.job && <FormHelperText>{problems.job}</FormHelperText>}
        </FormControl>
      </Box>

      {draft.kind === 'table' ? (
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.5 }}>
            Tables {draft.tables.length > 1 ? `(${draft.tables.length}, all on this job)` : ''}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }} aria-label="Chosen tables">
            {draft.tables.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Pick one or more saved tables below.</Typography>
            ) : draft.tables.map((t) => (
              <Chip
                key={tablePath(t)}
                size="small"
                label={t.display_name || t.filename}
                title={tablePath(t)}
                onDelete={off ? undefined : () => onRemoveTable(t)}
                sx={{ borderRadius: INNER_RADIUS }}
              />
            ))}
          </Box>
          <Paper
            variant="outlined"
            aria-label="Tables"
            sx={{ p: 1, height: 200, overflow: 'auto', border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: INNER_RADIUS, opacity: off ? 0.6 : 1, pointerEvents: off ? 'none' : 'auto' }}
          >
            {!draft.job ? (
              <Typography variant="body2" color="text.secondary">Pick a job first.</Typography>
            ) : tocLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>
            ) : (
              <Tree
                searchEnabled
                searchPlaceholder="Search tables..."
                nodes={toc}
                expanded={expanded}
                selected={selected}
                onNodeSelectToggle={handleNodeSelect}
                onNodeExpandToggle={handleNodeExpand}
              />
            )}
          </Paper>
          {problems.tables && <FormHelperText error>{problems.tables}</FormHelperText>}
        </Box>
      ) : (
        <TextField
          label="Question"
          placeholder="e.g. NPS among 18–34s in the latest wave, by region"
          size="small"
          value={draft.question}
          onChange={(e) => onChange({ question: e.target.value })}
          disabled={off}
          required
          multiline
          minRows={2}
          error={Boolean(problems.question)}
          helperText={problems.question || "The agent turns it into a crosstab of the job's data."}
          sx={SMALL_FIELD_SX}
        />
      )}

      <TextField
        label="Tell me when"
        placeholder="e.g. the renewal rate falls below 60% in the latest month"
        size="small"
        value={draft.condition}
        onChange={(e) => onChange({ condition: e.target.value })}
        disabled={off}
        required
        multiline
        minRows={2}
        error={Boolean(problems.condition)}
        helperText={problems.condition}
        sx={SMALL_FIELD_SX}
      />

      <TextField
        label="Context (optional)"
        placeholder="Anything the agent should know: which rows matter, what counts as a change"
        size="small"
        value={draft.context}
        onChange={(e) => onChange({ context: e.target.value })}
        disabled={off}
        multiline
        minRows={1}
        sx={SMALL_FIELD_SX}
      />

      <Box>
        <AlertSchedulePicker value={draft.schedule} onChange={(schedule: AlertSchedule) => onChange({ schedule })} disabled={off} />
        {problems.schedule && <FormHelperText error>{problems.schedule}</FormHelperText>}
      </Box>

      <AlertRecipientsField
        value={draft.recipients}
        onChange={(recipients) => onChange({ recipients })}
        options={recipientOptions}
        loading={recipientsLoading}
        error={recipientsError}
        inputValue={recipientQuery}
        onInputChange={onRecipientQueryChange}
        disabled={off}
      />
    </Box>
  )
}

export default AlertSetupCard
